import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RiSearchLine, RiFilter3Line } from 'react-icons/ri';
import axios from 'axios';
import './PrescriptionManagement.css';
import PrescriptionModal from './PrescriptionModal';

const PrescriptionManagement = () => {
    const [prescriptions, setPrescriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [selectedPrescription, setSelectedPrescription] = useState(null);

    useEffect(() => {
        fetchPrescriptions();
    }, []);

    const fetchPrescriptions = async () => {
        try {
            const response = await axios.get('https://api.swasthmitra.com/api/prescription/all');
            setPrescriptions(response.data.prescriptions);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching prescriptions:', error);
            setLoading(false);
        }
    };

    const updateStatus = async (id, newStatus) => {
        try {
            await axios.patch(`https://api.swasthmitra.com/api/prescription/${id}/status`, {
                status: newStatus
            });
            fetchPrescriptions();
        } catch (error) {
            console.error('Error updating status:', error);
        }
    };

    const filteredPrescriptions = prescriptions
        .filter(prescription => {
            const matchesSearch = (
                (prescription.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (prescription.phone || '').includes(searchTerm)
            );
            const matchesFilter = filterStatus === 'all' || prescription.status === filterStatus;
            return matchesSearch && matchesFilter;
        })
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending':
                return 'status-pending';
            case 'processed':
                return 'status-processed';
            case 'completed':
                return 'status-completed';
            default:
                return 'status-default';
        }
    };

    return (
        <div className="prescription-management">
            <div className="header">
                <h2>Prescription Management</h2>
                <div className="filters">
                    <div className="search-box">
                        <RiSearchLine />
                        <input
                            type="text"
                            placeholder="Search by name or phone..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="status-filter">
                        <RiFilter3Line />
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                        >
                            <option value="all">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="processed">Processed</option>
                            <option value="completed">Completed</option>
                        </select>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loading">Loading prescriptions...</div>
            ) : (
                <div className="prescriptions-grid">
                    {filteredPrescriptions.map((prescription) => (
                        <motion.div
                            key={prescription._id}
                            className="prescription-card"
                            whileHover={{ scale: 1.02 }}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            <div className="card-header">
                                <h3>{prescription.name || 'No Name'}</h3>
                                <span className={`status ${getStatusColor(prescription.status)}`}>
                                    {prescription.status || 'pending'}
                                </span>
                            </div>
                            <div className="card-body">
                                <p><strong>Phone:</strong> {prescription.phone || 'No Phone'}</p>
                                <p><strong>Date:</strong> {new Date(prescription.createdAt).toLocaleDateString()}</p>
                                <div className="prescription-image">
                                    <img
                                        src={prescription.prescriptionImage || 'default-image-url'}
                                        alt="Prescription"
                                        onError={(e) => {
                                            e.target.src = 'default-image-url'; // Add a default image URL
                                            e.target.onerror = null; // Prevent infinite loop
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="card-footer">
                                <select
                                    value={prescription.status}
                                    onChange={(e) => updateStatus(prescription._id, e.target.value)}
                                    className="status-select"
                                >
                                    <option value="pending">Pending</option>
                                    <option value="processed">Processed</option>
                                    <option value="completed">Completed</option>
                                </select>
                                <button
                                    className="view-details"
                                    onClick={() => setSelectedPrescription(prescription)}
                                >
                                    View Details
                                </button>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}

            {selectedPrescription && (
                <AnimatePresence>
                    <PrescriptionModal
                        prescription={selectedPrescription}
                        onClose={() => setSelectedPrescription(null)}
                        onUpdateStatus={updateStatus}
                    />
                </AnimatePresence>
            )}
        </div>
    );
};

export default PrescriptionManagement; 